import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { ClipRequest, YouTubeUrl, youtubeVideoId } from "@mdj/shared";
import { api, type Probe } from "../lib/api";
import { Button, GlassCard } from "../components/Glass";
import { Trimmer } from "../components/Trimmer";

export function SongStep({ onCut, initialUrl = "" }: { onCut: (req: ClipRequest) => void; initialUrl?: string }) {
  const [url, setUrl] = useState(initialUrl);
  const [err, setErr] = useState<string | null>(null);
  const [probe, setProbe] = useState<Probe | null>(null);
  const [probing, setProbing] = useState(false);
  const [start, setStart] = useState(0);
  const [length, setLength] = useState(10);
  const [previewing, setPreviewing] = useState(false);

  // Probe as soon as the pasted text is a YouTube link.
  useEffect(() => {
    const parsed = YouTubeUrl.safeParse(url);
    if (!parsed.success) { setProbe(null); setErr(url ? "Paste a YouTube link" : null); return; }
    setErr(null);
    let live = true; setProbing(true);
    api.probe(parsed.data)
      .then((p) => { if (live) { setProbe(p); setStart(0); setPreviewing(false); } })
      .catch((e: Error) => { if (live) setErr(e.message); })
      .finally(() => { if (live) setProbing(false); });
    return () => { live = false; };
  }, [url]);

  const paste = async () => {
    try { const t = await navigator.clipboard.readText(); if (t) setUrl(t); } catch { /* clipboard denied: user types */ }
  };

  const videoId = probe?.videoId ?? youtubeVideoId(url);
  const stop = probe ? Math.min(probe.durationSeconds, start + length) : start + length;

  return (
    <GlassCard key="song">
      <h2>Pick the song</h2>
      <p className="hint">Paste a YouTube link, then slide the window to the part that hits.</p>

      <label className="field">
        <input
          inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false}
          placeholder="https://youtu.be/…" value={url} onChange={(e) => setUrl(e.target.value)}
          aria-label="YouTube link" aria-invalid={Boolean(err)}
        />
        {url ? (
          <Button ghost icon aria-label="Clear" onClick={() => setUrl("")}>✕</Button>
        ) : (
          <Button ghost onClick={paste}>Paste</Button>
        )}
      </label>
      <AnimatePresence>{err && <motion.p className="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ margin: 0, color: "var(--danger)", fontSize: 13 }}>{err}</motion.p>}</AnimatePresence>

      <AnimatePresence mode="popLayout">
        {videoId && (
          <motion.div key={videoId} className={previewing ? "video" : "video compact"} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}>
            {previewing ? (
              <iframe
                title="Preview"
                src={`https://www.youtube-nocookie.com/embed/${videoId}?start=${Math.floor(start)}&end=${Math.ceil(stop)}&autoplay=1&playsinline=1&controls=0&rel=0`}
                allow="autoplay; encrypted-media" />
            ) : (
              <>
                <img src={`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`} alt="" />
                <div className="title">{probe?.title ?? "…"}</div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {probe && (
          <motion.div key="trim" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} style={{ overflow: "hidden", display: "flex", flexDirection: "column", gap: 14 }}>
            <Trimmer duration={probe.durationSeconds} start={start} length={length} onChange={(s, l) => { setStart(s); setLength(l); setPreviewing(false); }} />
            <div className="row">
              <Button onClick={() => setPreviewing((p) => !p)}>{previewing ? "Stop" : "▶ Preview"}</Button>
              <Button primary onClick={() => onCut({ url: YouTubeUrl.parse(url), window: { start, stop } })}>Cut it</Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {probing && !probe && <p className="hint">Looking it up…</p>}
    </GlassCard>
  );
}
